import { Mail } from "lucide-react";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import { MaskedLine } from "@/components/MaskedLine";

const FAQS = [
  {
    q: "What features does First Class offer?",
    a: "First Class adds key features for travelers including thirty Tripsee Timelines per month, Flight Reliability ratings, Baggage Buddy AI Smart Suggestions, and the Gate Guide. Coach includes the essentials: six Timelines per month, Flight Assistance, Baggage Buddy, and real-time alerts.",
  },
  {
    q: "I purchased First Class, but the features aren't showing up as unlocked.",
    a: "Though uncommon, this does happen from time to time. The most common fix is to close the app completely and relaunch it. If you're still having trouble, reach out to support@tripsee.io and we'll happily resolve it.",
  },
  {
    q: "I created a trip, deleted it, and it still counts against my monthly limit.",
    a: "Every trip created counts against your monthly allowance, even if you delete it. Coach includes six Tripsee Timelines per month and First Class includes thirty, so you can recreate the trip if needed.",
  },
  {
    q: "The Tripsee Timeline for my trip was inaccurate.",
    a: "Tripsee calculates recommended departure times from several live travel variables, and times can shift up to your day of travel as conditions change. Check your Timeline on the day of travel for the most current times, and use the in-app feedback icon to report inaccuracies. Crowdsourced data makes every future prediction better.",
  },
  {
    q: "Is Tripsee available on both iOS and Android?",
    a: "Yes. Tripsee is available on the Apple App Store and Google Play through one smart download link. The app detects your device and takes you to the right store.",
  },
  {
    q: "How does Tripsee handle my location data?",
    a: "Your location is used to compute your transit route during active trips. For full detail, see our Privacy Policy, linked in the footer of every page.",
  },
];

export default function Faqs() {
  return (
    <main>
      <Seo
        title="FAQs — Tripsee"
        description="Answers about Tripsee plans, Tripsee Timelines, First Class features, availability on iOS and Android, and how your data is handled."
      />

      <section className="relative pt-40 pb-20 overflow-hidden bg-radar-grid">
        <div className="absolute inset-0 ambient-glow" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="font-mono text-xs tracking-[0.3em] text-primary uppercase mb-6">Support</p>
          <h1 className="font-display text-4xl sm:text-6xl lg:text-7xl font-extrabold tracking-tight leading-[1.05] text-white">
            <MaskedLine delay={0.1}>Frequently asked</MaskedLine>
            <MaskedLine delay={0.26} className="text-primary text-glow">questions.</MaskedLine>
          </h1>
        </div>
      </section>

      <section className="py-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <Accordion type="single" collapsible className="space-y-4">
              {FAQS.map((f, i) => (
                <AccordionItem
                  key={i}
                  value={`faq-${i}`}
                  data-testid={`faq-accordion-item-${i}`}
                  className="glass-panel rounded-2xl border-white/10 px-6 overflow-hidden"
                >
                  <AccordionTrigger className="text-left font-display text-base sm:text-lg font-bold text-white hover:text-primary hover:no-underline py-6">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-400 leading-relaxed pb-6">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>

          <Reveal delay={0.15} className="mt-12">
            <div className="glass-panel rounded-2xl p-8 text-center">
              <h3 className="font-display text-xl font-bold text-white">Still stuck at the gate?</h3>
              <p className="mt-3 text-sm text-slate-400">
                Our support crew is happy to help with anything else.
              </p>
              <a
                href="mailto:support@tripsee.io"
                data-testid="faqs-support-email-button"
                className="mt-6 inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-bold text-[#0B0B0D] transition-transform duration-200 hover:scale-[1.04] active:scale-95"
              >
                <Mail size={15} /> support@tripsee.io
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
